import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Gift } from "lucide-react";
import { StoreButtons } from "./StoreButtons";
import { PhoneMockup } from "./PhoneMockup";
import { BRAND } from "../../data/content";
import heroFood from "../../assets/hero-food-new.png";
import heroBody from "../../assets/hero-body-new.png";
import heroPlate from "../../assets/hero-plate-new.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -65]);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24">
      <div className="pointer-events-none absolute -top-24 -left-20 h-[380px] w-[380px] rounded-full bg-food-bg blur-[110px] opacity-60" />
      <div className="pointer-events-none absolute top-32 -right-10 h-[360px] w-[360px] rounded-full bg-bodycare-bg blur-[110px] opacity-60" />

      <div className="relative mx-auto max-w-[1240px] px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-food-accent/30 bg-food-bg px-3.5 py-1.5 text-xs font-semibold text-food-text shadow-soft"
          >
            <Gift className="h-3.5 w-3.5 text-food-accent" />
            Try Before You Buy! 4 FREE Scans · No Credit Card Required
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="text-4xl sm:text-5xl lg:text-[3.6rem] font-bold leading-[1.05] text-ink"
          >
            {BRAND.tagline.split(",")[0]},
            <br />
            {BRAND.tagline.split(",").slice(1).join(",").trim()}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="mt-6 max-w-xl space-y-4 text-base md:text-lg leading-relaxed text-slate"
          >
            <p>Take the mystery out of what goes into your body and onto your skin.</p>
            <p>
              WhatThe<span style={{ color: "#16A34A" }}>?</span> Scan includes two
              ingredient scanners — WhatThe<span style={{ color: "#F97316" }}>?</span> Food and
              WhatThe<span style={{ color: "#8B5CF6" }}>?</span> Body.
            </p>
            <p>
              Each scanner analyses ingredient lists and explains what a product
              is, what's in it and how it may affect you. In plain
              English. The Food scanner can even analyse a plate of freshly cooked food.
            </p>
            <p>
              Not sure which one you need? Try before you buy with 4 free scans
              across both scanners. Then simply purchase the one you love (or both).
              No subscription, ever.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-8"
          >
            <div>
              <div
                data-testid="free-scans-callout"
                className="mb-4 inline-flex items-center gap-2.5 rounded-2xl border border-food-accent/30 bg-food-bg px-4 py-2.5 shadow-soft"
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-food-accent">
                  <Gift className="h-4 w-4 text-white" strokeWidth={2} />
                </span>
                <p className="text-sm leading-snug text-food-text">
                  <span className="font-bold">Get 4 free scans</span> the moment you download —
                  <span className="font-semibold"> no account required.</span>
                </p>
              </div>
              <StoreButtons />
              <p className="mt-3 text-sm text-slate">
                4 free scans · No account · No subscription
              </p>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="relative flex items-end justify-center gap-3 md:gap-5">
            <motion.div
              style={{ y: y1 }}
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.45, duration: 0.9, ease }}
              className="w-[46%] max-w-[200px] z-10"
            >
              <PhoneMockup src={heroFood} alt="WhatThe? Food scanner" className="!w-full shadow-soft-lg" />
            </motion.div>

            <motion.div
              style={{ y: y2 }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.9, ease }}
              className="block w-[46%] max-w-[200px]"
            >
              <PhoneMockup src={heroPlate} alt="WhatThe? Food plate scanner" className="!w-full opacity-95" />
            </motion.div>

            <motion.div
              style={{ y: y3 }}
              initial={{ opacity: 0, y: 55 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.9, ease }}
              className="block w-[46%] max-w-[200px]"
            >
              <PhoneMockup src={heroBody} alt="WhatThe? Body scanner" className="!w-full opacity-95" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
